/**
 * Admin event editor — create / edit / delete events with agenda.
 *
 * Route: /admin/events
 *
 * Pairs with spec 080's PageEditor (each event auto-creates a landing Page;
 * admins edit its parts via PageEditor).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiClientError,
  chaptersApi,
  eventsApi,
  type AgendaItem,
  type BranchSummary,
  type ChapterSummary,
  type EventRegistrationMode,
  type EventV2,
  type ScopeType,
} from '@/services/api';

export function EventEditorPage() {
  // Scope
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [scopeType, setScopeType] = useState<ScopeType>('chapter');
  const [chapterId, setChapterId] = useState('');
  const [branchId, setBranchId] = useState('');

  // Events list + selection
  const [events, setEvents] = useState<EventV2[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [timezone, setTimezone] = useState('');
  const [locationText, setLocationText] = useState('');
  const [registrationMode, setRegistrationMode] = useState<EventRegistrationMode>('none');
  const [capacity, setCapacity] = useState<string>('');
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const selectedScopeId = useMemo(() => (scopeType === 'branch' ? branchId : chapterId), [scopeType, chapterId, branchId]);

  useEffect(() => {
    chaptersApi.list('navarasam').then((rows) => {
      setChapters(rows);
      if (rows.length > 0 && !chapterId) setChapterId(rows[0]!.id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chapterId) return;
    chaptersApi.listBranches(chapterId).then((rows) => {
      setBranches(rows);
      if (scopeType === 'branch' && rows.length > 0 && !branchId) setBranchId(rows[0]!.id);
    });
  }, [chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    if (!selectedScopeId) return;
    try {
      const rows = await eventsApi.list(scopeType, selectedScopeId);
      setEvents(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    }
  }, [scopeType, selectedScopeId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function resetForm() {
    setSelectedId(null);
    setSlug('');
    setTitle('');
    setStartsAt('');
    setEndsAt('');
    setTimezone('');
    setLocationText('');
    setRegistrationMode('none');
    setCapacity('');
    setAgenda([]);
  }

  function loadIntoForm(e: EventV2) {
    setSelectedId(e.id);
    setSlug(e.slug);
    setTitle(e.title);
    setStartsAt(new Date(e.startsAt).toISOString().slice(0, 16));
    setEndsAt(e.endsAt ? new Date(e.endsAt).toISOString().slice(0, 16) : '');
    setTimezone(e.timezone ?? '');
    setLocationText(e.locationText ?? '');
    setRegistrationMode(e.registrationMode);
    setCapacity(e.capacity?.toString() ?? '');
    setAgenda(e.agenda ?? []);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const base = {
        slug,
        title,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
        timezone: timezone || undefined,
        locationText: locationText || undefined,
        registrationMode,
        capacity: capacity ? Number(capacity) : undefined,
        agenda: agenda.length > 0 ? agenda : undefined,
      };
      if (selectedId) {
        await eventsApi.update(selectedId, base);
        setSaveMessage('Updated.');
      } else {
        const created = await eventsApi.create({ scopeType, scopeId: selectedScopeId, ...base });
        setSaveMessage('Created.');
        setSelectedId(created.id);
      }
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError('Failed to save');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 2500);
    }
  }

  async function handleDelete() {
    if (!selectedId) return;
    if (!confirm('Delete this event? This cannot be undone.')) return;
    try {
      await eventsApi.remove(selectedId);
      resetForm();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }

  function addAgendaRow() {
    setAgenda((prev) => [...prev, { time: '', title: '' }]);
  }
  function updateAgenda(i: number, key: keyof AgendaItem, value: string) {
    setAgenda((prev) => prev.map((a, idx) => (idx === i ? { ...a, [key]: value } : a)));
  }
  function removeAgenda(i: number) {
    setAgenda((prev) => prev.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold text-white">Events</h1>
        <p className="text-white/60 mt-1">Create events, edit agendas, manage attendees.</p>
      </div>

      <section className="card">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Scope</label>
            <select value={scopeType} onChange={(e) => { setScopeType(e.target.value as ScopeType); resetForm(); }} className="input">
              <option value="chapter">Chapter</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Chapter</label>
            <select value={chapterId} onChange={(e) => { setChapterId(e.target.value); resetForm(); }} className="input">
              {chapters.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
            </select>
          </div>
          {scopeType === 'branch' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Branch</label>
              <select value={branchId} onChange={(e) => { setBranchId(e.target.value); resetForm(); }} className="input" disabled={branches.length === 0}>
                {branches.length === 0 ? <option value="">— No branches —</option> : branches.map((b) => <option key={b.id} value={b.id}>{b.displayName ?? b.name}</option>)}
              </select>
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">{selectedId ? 'Edit event' : 'New event'}</h2>
          {selectedId && (
            <button type="button" onClick={resetForm} className="text-xs text-white/70 hover:text-white">+ New event instead</button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/80 mb-1">Slug</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="input" placeholder="meet-2026" />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Starts at</label>
            <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="input" />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Ends at (optional)</label>
            <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="input" />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Timezone (optional)</label>
            <input value={timezone} onChange={(e) => setTimezone(e.target.value)} className="input" placeholder="Asia/Kolkata" />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Location</label>
            <input value={locationText} onChange={(e) => setLocationText(e.target.value)} className="input" />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Registration mode</label>
            <select value={registrationMode} onChange={(e) => setRegistrationMode(e.target.value as EventRegistrationMode)} className="input">
              <option value="none">none</option>
              <option value="open">open</option>
              <option value="invitee_only">invitee_only</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Capacity (optional)</label>
            <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} className="input" />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-white">Agenda</h3>
            <button type="button" onClick={addAgendaRow} className="btn-secondary text-xs">+ Row</button>
          </div>
          {agenda.length === 0 ? (
            <p className="text-white/60 text-sm">No agenda rows yet.</p>
          ) : (
            <div className="space-y-2">
              {agenda.map((row, i) => (
                <div key={i} className="grid md:grid-cols-12 gap-2 items-center">
                  <input value={row.time} onChange={(e) => updateAgenda(i, 'time', e.target.value)} placeholder="17:00" className="input md:col-span-2" />
                  <input value={row.title} onChange={(e) => updateAgenda(i, 'title', e.target.value)} placeholder="Title" className="input md:col-span-3" />
                  <input value={row.description ?? ''} onChange={(e) => updateAgenda(i, 'description', e.target.value)} placeholder="Description (optional)" className="input md:col-span-6" />
                  <button type="button" onClick={() => removeAgenda(i)} className="text-xs text-red-400 hover:text-red-300 md:col-span-1">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

        <div className="mt-6 flex items-center gap-3">
          <button type="button" onClick={handleSave} disabled={saving || !selectedScopeId} className="btn-primary">
            {saving ? 'Saving…' : selectedId ? 'Update event' : 'Create event'}
          </button>
          {selectedId && (
            <button type="button" onClick={handleDelete} className="text-xs text-red-400 hover:text-red-300">Delete event</button>
          )}
          {saveMessage && <span className="text-green-400 text-sm">{saveMessage}</span>}
        </div>
      </section>

      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-4">Existing events</h2>
        {events.length === 0 ? (
          <p className="text-white/60">No events in this scope yet.</p>
        ) : (
          <ul className="divide-y divide-white/10">
            {events.map((e) => (
              <li
                key={e.id}
                onClick={() => loadIntoForm(e)}
                className={`py-2 px-2 cursor-pointer flex items-center justify-between rounded ${
                  selectedId === e.id ? 'bg-primary/10' : 'hover:bg-white/5'
                }`}
              >
                <div>
                  <p className="text-white">{e.title}</p>
                  <p className="text-xs text-white/40">{new Date(e.startsAt).toLocaleString()} · /{e.slug} · {e.registrationMode}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default EventEditorPage;
